import Div from "@jumbo/shared/Div/Div";
import ClearIcon from "@mui/icons-material/Clear";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import {makeStyles} from "@mui/styles";
import MuiDatepicker from "app/pages/Docgen/components/common/mui-datepicker/MuiDatepicker";
import {Date_format} from "app/pages/Docgen/constants/date_format";
import {fetchApplicationType, fetchEntityStatus, fetchMatterStatus, fetchMatterSubMatterType} from "app/utils/constants/fetchMappingData";
import {useFormik} from "formik";
import moment from "moment";
import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {v4 as uuidV4} from "uuid";
import * as Yup from "yup";
import {get} from "../../../common/network";
import {VALIDATION_MESSAGE} from "../../../constants/index";
import {length_validation} from "../../../constants/validation";


const MatterDetails = ({setActiveTab, setFormData, formData, matterId, setMatterId, setCombinedMatterData}) => {
    const [loading, setLoading] = useState(false);
    const [matterStatusMapping, setMatterStatusMapping] = useState({});
    const [applicationTypeMapping, setApplicationTypeMapping] = useState({});
    const [matterSubMatterTypeMapping, setMatterSubMatterTypeMapping] = useState({});
    const [entityStatusMapping, setEntityStatusMapping] = useState({});
    const [countriesMapping, setCountriesMapping] = useState([]);

    const fetchCountryData = async () => {
        const data = await get("docgen/countries");
        setCountriesMapping(data);
    };

    useEffect(() => {
        const getMappings = async () => {
            try {
                const [matterStatusResponse, applicationTypeResponse, matterSubMatterTypeResponse, entityStatusResponse] = await Promise.all([fetchMatterStatus(), fetchApplicationType(), fetchMatterSubMatterType(), fetchEntityStatus()]);
                setMatterStatusMapping(matterStatusResponse);
                setApplicationTypeMapping(applicationTypeResponse);
                setMatterSubMatterTypeMapping(matterSubMatterTypeResponse);
                setEntityStatusMapping(entityStatusResponse);
            } catch (error) {
            }
        };
        getMappings();
        fetchCountryData();
    }, []);

    //#region for handling form validation and submission
    const handleClearGrantDate = () => {
        formik.setFieldValue("grant_date", null); // Clear the date field
    };

    const handleClearFilingDate = () => {
        formik.setFieldValue("filing_date", null); // Clear the date field
    };

    const handleClearLocalFilingDate = () => {
        formik.setFieldValue("local_filing_date", null); // Clear the date field
    };

    const handleClearPriorityDate = () => {
        formik.setFieldValue("priority_date", null); // Clear the date field
    };

    const handleClearPCTDate = () => {
        formik.setFieldValue("pct_date", null); // Clear the date field
    };

    const handleClearDate = field => () => formik.setFieldValue(field, null);

    // Validation schema defined
    const validationSchema = Yup.object({
        host_file_number: Yup.string()
                             .trim("No leading/trailing spaces allowed")
                             .max(30, length_validation.host_file_number)
                             .required(VALIDATION_MESSAGE.required),
        application_number: Yup.string()
                               .nullable()
                               .max(30, length_validation.application_number)
                               .typeError(VALIDATION_MESSAGE.string),
        title: Yup.string()
                  .trim("No leading/trailing spaces allowed")
                  .required(VALIDATION_MESSAGE.required)
                  .max(250, length_validation.title)
                  .typeError(VALIDATION_MESSAGE.string),

        status: Yup.string().nullable().typeError(VALIDATION_MESSAGE.string),
        matter_sub_matter_type: Yup.string()
                                   .required(VALIDATION_MESSAGE.required)
                                   .typeError(VALIDATION_MESSAGE.string),
        matter_type: Yup.string().required(VALIDATION_MESSAGE.required).typeError(VALIDATION_MESSAGE.string),
        correspondence_customer_no: Yup.string()
                                       .nullable()
                                       .max(100, length_validation.correspondence_customer_no)
                                       .nullable()
                                       .typeError(VALIDATION_MESSAGE.string),
        art_unit: Yup.string().nullable(),
        confirmation_number: Yup.string()
                                .nullable()
                                .transform((value, originalValue) => (!originalValue ? null : value))
                                .typeError("Confirmation Number should be a type of string"),
        matter_entity_size: Yup.string()
                               .nullable()
                               .typeError(VALIDATION_MESSAGE.string),
        pct_application_no: Yup.string()
                               .max(30, length_validation.pct_application_no)
                               .nullable()
                               .typeError(VALIDATION_MESSAGE.string),
        patent_number: Yup.string()
                          .max(30, length_validation.patent_number)
                          .nullable()
                          .typeError(VALIDATION_MESSAGE.string),
        deposit_acc_no: Yup.string()
                           .max(50, length_validation.deposit_acc_no)
                           .nullable()
                           .typeError(VALIDATION_MESSAGE.string),
        publicationNumber: Yup.string()
                              .nullable()
                              .typeError(VALIDATION_MESSAGE.string),
        agentReferenceNumber: Yup.string()
                                 .nullable()
                                 .typeError(VALIDATION_MESSAGE.string),
        clientReferenceNumber: Yup.string()
                                  .nullable()
                                  .typeError(VALIDATION_MESSAGE.string),
        countryId: Yup.number()
                      .required(VALIDATION_MESSAGE.required)
                      .typeError(VALIDATION_MESSAGE.number)
    });

    // Added formik for form and fields handling
    const formik = useFormik({
        initialValues: {
            ...formData,
            host_file_number: formData?.host_file_number
                ? formData.host_file_number
                : "",
            filing_date: formData?.filing_date ? moment(formData.filing_date) : null,
            local_filing_date: formData?.local_filing_date
                ? moment(formData.local_filing_date)
                : null,
            priority_date: formData?.priority_date
                ? moment(formData.priority_date)
                : null,
            pct_date: formData?.pct_date ? moment(formData.pct_date) : null,
            grant_date: formData?.grant_date ? moment(formData.grant_date) : null,
            publicationDate: formData?.publicationDate ? moment(formData.publicationDate) : null,
            expiryDate: formData?.expiryDate ? moment(formData.expiryDate) : null
        } || {
            host_file_number: "",
            title: "",
            application_number: null,
            status: "",
            filing_date: null,
            local_filing_date: null,
            matter_sub_matter_type: null,
            matter_type: null,
            correspondence_customer_no: "",
            art_unit: null,
            confirmation_number: null,
            matter_entity_size: null,
            priority_date: null,
            pct_application_no: "",
            pct_date: null,
            patent_number: "",
            grant_date: null,
            deposit_acc_no: "",
            countryId: null
        },
        validationSchema: validationSchema,
        onSubmit: async values => {
            values = Object.keys(values).reduce((result, key) => {
                result[key] = typeof values[key] === "string" ? values[key].trim() : values[key];
                return result;
            }, {});

            // Format the dates in the desired format "MM-DD-YYYY"
            const formattedLocalFillingDate = values?.local_filing_date?.format(Date_format.date_format_search) || null;
            const formattedFillingDate = values?.filing_date?.format(Date_format.date_format_search) || null;
            const formattedGrantDate = values?.grant_date?.format(Date_format.date_format_search) || null;
            const formattedPctFilingDate = values?.pct_date?.format(Date_format.date_format_search) || null;
            const formattedPriorityDate = values?.priority_date?.format(Date_format.date_format_search) || null;
            const formattedPublicationDate = values?.publicationDate?.format(Date_format.date_format_search) || null;
            const formattedExpiryDate = values?.expiryDate?.format(Date_format.date_format_search) || null;

            if (!values.confirmation_number) {
                values.confirmation_number = null;
            }

            if (!matterId) {
                setLoading(true);
                try {
                    setCombinedMatterData({
                        ...values,
                        local_filing_date: formattedLocalFillingDate,
                        filing_date: formattedFillingDate,
                        grant_date: formattedGrantDate,
                        pct_date: formattedPctFilingDate,
                        priority_date: formattedPriorityDate,
                        publicationDate: formattedPublicationDate,
                        expiryDate: formattedExpiryDate
                    });
                    toast.success("Matter created successfully");
                    const tempId = uuidV4();
                    setMatterId(tempId);
                    setFormData(formik?.values);
                    setActiveTab(1);

                } catch (error) {
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(true);
                setCombinedMatterData({
                    ...values,
                    local_filing_date: formattedLocalFillingDate,
                    filing_date: formattedFillingDate,
                    grant_date: formattedGrantDate,
                    pct_date: formattedPctFilingDate,
                    priority_date: formattedPriorityDate,
                    publicationDate: formattedPublicationDate,
                    expiryDate: formattedExpiryDate
                });
                toast.success("Matter created successfully");
                setFormData(formik?.values);
                // Passing to next tab
                setActiveTab(1);
                setLoading(false);
            }
        }
    });

    const useStyles = makeStyles(() => ({
        menuPaper: {
            backgroundColor: "white",
            color: "black"
        }
    }));

    const classes = useStyles();

    return (
        <Div>
            <form onSubmit={formik.handleSubmit}>
                <div className="form-action-area container mb-0">
                    {loading ? <CircularProgress/> : <Button type="submit" variant="contained" className="btn-theme">Save & Next</Button>}
                </div>
                <div className="container">
                    <h6 className="form-sub-heading mb-3">Bibliographic</h6>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="host_file_number"
                                    name="host_file_number"
                                    label="Attorney Docket Number *"
                                    value={formik.values.host_file_number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.host_file_number)}
                                    helperText={formik.errors.host_file_number}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="application_number"
                                    name="application_number"
                                    label="Application Number"
                                    value={formik.values.application_number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.application_number && Boolean(formik.errors.application_number)}
                                    helperText={formik.touched.application_number && formik.errors.application_number}
                                />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="form-group">
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="title"
                                    name="title"
                                    label="Title *"
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.title)}
                                    helperText={formik.errors.title}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",
                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="filing_date"
                                        name="filing_date"
                                        label="Filing Date"
                                        value={formik.values.filing_date}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("filing_date", value ? moment(value) : null);
                                        }}
                                        error={
                                            formik.touched.filing_date &&
                                            Boolean(formik.errors.filing_date)
                                        }
                                        helperText={
                                            formik.touched.filing_date && formik.errors.filing_date
                                        }
                                        renderInput={(params) => <TextField  {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearFilingDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",

                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="local_filing_date"
                                        name="local_filing_date"
                                        label="Local Filing Date"
                                        value={formik.values.local_filing_date}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("local_filing_date", value ? moment(value) : null);
                                        }}
                                        error={
                                            formik.touched.local_filing_date &&
                                            Boolean(formik.errors.local_filing_date)
                                        }
                                        helperText={
                                            formik.touched.local_filing_date &&
                                            formik.errors.local_filing_date
                                        }
                                        renderInput={(params) => <TextField  {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearLocalFilingDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField fullWidth size="small" id="publicationNumber" name="publicationNumber" label="Publication Number" value={formik.values.publicationNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.publicationNumber && Boolean(formik.errors.publicationNumber)} helperText={formik.touched.publicationNumber && formik.errors.publicationNumber}/>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",

                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="publicationDate"
                                        name="publicationDate"
                                        label="Publication Date"
                                        value={formik.values.publicationDate}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("publicationDate", value ? moment(value) : null);
                                        }}
                                        error={
                                            formik.touched.publicationDate &&
                                            Boolean(formik.errors.publicationDate)
                                        }
                                        helperText={
                                            formik.touched.publicationDate &&
                                            formik.errors.publicationDate
                                        }
                                        renderInput={(params) => <TextField  {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearDate("publicationDate")}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField fullWidth size="small" id="clientReferenceNumber" name="clientReferenceNumber" label="Client Reference Number" value={formik.values.clientReferenceNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.clientReferenceNumber && Boolean(formik.errors.clientReferenceNumber)} helperText={formik.touched.clientReferenceNumber && formik.errors.clientReferenceNumber}/>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField fullWidth size="small" id="agentReferenceNumber" name="agentReferenceNumber" label="Agent Reference Number" value={formik.values.agentReferenceNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.agentReferenceNumber && Boolean(formik.errors.agentReferenceNumber)} helperText={formik.touched.agentReferenceNumber && formik.errors.agentReferenceNumber}/>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="confirmation_number"
                                    name="confirmation_number"
                                    label="Confirmation Number"
                                    value={formik.values.confirmation_number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.confirmation_number && Boolean(formik.errors.confirmation_number)}
                                    helperText={formik.touched.confirmation_number && formik.errors.confirmation_number}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="art_unit"
                                    name="art_unit"
                                    label="Art Unit"
                                    value={formik.values.art_unit}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.art_unit && Boolean(formik.errors.art_unit)}
                                    helperText={formik.touched.art_unit && formik.errors.art_unit}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField
                                    fullWidth
                                    size="small"
                                    id="patent_number"
                                    name="patent_number"
                                    label="Patent Number"
                                    value={formik.values.patent_number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.patent_number && Boolean(formik.errors.patent_number)}
                                    helperText={formik.touched.patent_number && formik.errors.patent_number}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",

                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="grant_date"
                                        name="grant_date"
                                        label="Issued Date"
                                        format="MM-DD-YYYY"
                                        value={formik.values.grant_date}
                                        onChange={(value) => {
                                            formik.setFieldValue("grant_date", value ? moment(value) : null);
                                        }}
                                        error={
                                            formik.touched.grant_date &&
                                            Boolean(formik.errors.grant_date)
                                        }
                                        helperText={
                                            formik.touched.grant_date && formik.errors.grant_date
                                        }
                                        renderInput={(params) => <TextField {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearGrantDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="matter_sub_matter_type-label">
                                        Application Type *
                                    </InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="matter_sub_matter_type-label"
                                        id="matter_sub_matter_type"
                                        name="matter_sub_matter_type"
                                        label="Application Type *"
                                        value={formik.values.matter_sub_matter_type ?? ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.matter_sub_matter_type)}
                                        helperText={formik.errors.matter_sub_matter_type}
                                    >
                                        {Object.entries(matterSubMatterTypeMapping).map(([key, value]) => { const label = typeof value === "string" ? value : key; return <MenuItem key={key} value={label}>{label}</MenuItem>; })}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="matter_type-label">Subject Matter *</InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="matter_type-label"
                                        id="matter_type"
                                        name="matter_type"
                                        label="Subject Matter *"
                                        value={formik.values.matter_type ?? ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.matter_type)}
                                        helperText={formik.errors.matter_type}
                                    >
                                        {Object.entries(applicationTypeMapping).map(([key, value]) => { const label = typeof value === "string" ? value : key; return <MenuItem key={key} value={label}>{label}</MenuItem>; })}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="status-label">Status</InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="status-label"
                                        id="status"
                                        name="status"
                                        label="Status"
                                        value={formik.values.status ?? ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.status && Boolean(formik.errors.status)}
                                        helperText={formik.touched.status && formik.errors.status}
                                    >
                                        {Object.entries(matterStatusMapping).map(([key, value]) => (
                                            <MenuItem key={key} value={value}>{value}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="matter_entity_size-label">
                                        Entity Status
                                    </InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="matter_entity_size-label"
                                        id="matter_entity_size"
                                        name="matter_entity_size"
                                        label="Entity Status"
                                        value={formik.values.matter_entity_size ?? ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.matter_entity_size && Boolean(formik.errors.matter_entity_size)}
                                        helperText={formik.touched.matter_entity_size && formik.errors.matter_entity_size}
                                    >
                                        {Object.entries(entityStatusMapping).map(([key, value]) => { const label = typeof value === "string" ? value : key; return <MenuItem key={key} value={label}>{label}</MenuItem>; })}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="country-label">Country *</InputLabel>
                                    <Select MenuProps={{classes: {paper: classes.menuPaper}}} labelId="country-label" label="Country *" id="countryId" name="countryId" value={formik.values.countryId} onChange={formik.handleChange} onBlur={formik.handleBlur} error={Boolean(formik.errors.countryId)} helperText={formik.errors.countryId}>
                                        {countriesMapping.map(country => (<MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField fullWidth size="small" id="deposit_acc_no" name="deposit_acc_no" label="Deposit Account No." value={formik.values.deposit_acc_no} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.deposit_acc_no && Boolean(formik.errors.deposit_acc_no)} helperText={formik.touched.deposit_acc_no && formik.errors.deposit_acc_no}/>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <TextField fullWidth size="small" id="correspondence_customer_no" name="correspondence_customer_no" label="Customer Number" value={formik.values.correspondence_customer_no} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.correspondence_customer_no && Boolean(formik.errors.correspondence_customer_no)} helperText={formik.touched.correspondence_customer_no && formik.errors.correspondence_customer_no}/>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",

                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="expiryDate"
                                        name="expiryDate"
                                        label="Expiry Date"
                                        value={formik.values.expiryDate}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("expiryDate", value ? moment(value) : null);
                                        }}
                                        error={
                                            formik.touched.expiryDate &&
                                            Boolean(formik.errors.expiryDate)
                                        }
                                        helperText={
                                            formik.touched.expiryDate &&
                                            formik.errors.expiryDate
                                        }
                                        renderInput={(params) => <TextField  {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearDate("expiryDate")}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <div className="form-group mt-4">
                                <TextField fullWidth size="small" id="pct_application_no" name="pct_application_no" label="PCT Application No" value={formik.values.pct_application_no} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.pct_application_no && Boolean(formik.errors.pct_application_no)} helperText={formik.touched.pct_application_no && formik.errors.pct_application_no}/>
                            </div>
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",
                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker id="pct_date" name="pct_date" label="PCT Filing Date" value={formik.values.pct_date} format="MM-DD-YYYY" onChange={(value) => {
                                        formik.setFieldValue("pct_date", value ? moment(value) : null);
                                    }} error={formik.touched.pct_date && Boolean(formik.errors.pct_date)} helperText={formik.touched.pct_date && formik.errors.pct_date} renderInput={(params) => <TextField {...params} />} maxDate={moment(new Date())}/>
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearPCTDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon style={{fontSize: "16px"}}/>
                                    </IconButton>
                                </Div>
                            </div>
                            <div className="form-group">
                                <TextField fullWidth size="small" id="priority_application_number" name="priority_application_number" label="Priority Application Claimed" value={formik.values.priority_application_number} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.priority_application_number && Boolean(formik.errors.priority_application_number)} helperText={formik.touched.priority_application_number && formik.errors.priority_application_number}/>
                            </div>
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",
                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="priority_date"
                                        name="priority_date"
                                        label="Priority Date Claimed"
                                        value={formik.values.priority_date}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("priority_date", value ? moment(value) : null);
                                        }}
                                        error={
                                            formik.touched.priority_date &&
                                            Boolean(formik.errors.priority_date)
                                        }
                                        helperText={
                                            formik.touched.priority_date &&
                                            formik.errors.priority_date
                                        }
                                        renderInput={(params) => <TextField {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearPriorityDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon style={{fontSize: "16px"}}/>
                                    </IconButton>
                                </Div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Div>
    );
};


export default MatterDetails;